import { make } from 'vuex-pathify'
import store from '../index'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'
import { formatDate, parseMoneyValue } from '@/util/formatters'
import axios from 'axios'
import { TIPO_DESPESA } from '@/enums/payments'

const normalizePaymentValues = payment => ({
  ...payment,
  value: parseMoneyValue(payment.value),
  valuePayed: parseMoneyValue(payment.valuePayed),
  discount: parseMoneyValue(payment.discount),
  addition: parseMoneyValue(payment.addition),
  costPaymentMethod: parseMoneyValue(payment.costPaymentMethod),
})

const normalizeFilter = filter => {
  const normalized = { ...filter }

  const situation = { Pago: 'paid', 'Em aberto': 'open', Vencido: 'overdue' }[normalized.status] ||
        normalized.status
  if (['open', 'overdue', 'paid'].includes(situation)) {
    normalized.situation = situation
    delete normalized.status
  }

  return normalized
}

const refreshPayments = () => Promise.all([
  store.dispatch('paymentStore/get'),
  store.dispatch('paymentStore/getSummary'),
])

const state = {
  payment: {},
  payments: {
    data: [],
    count: 0,
    limit: 50,
  },
  paymentSummary: {
    total: 0,
    paid: 0,
    open: 0,
    overdue: 0,
  },
  paymentOff: {},
  paymentsGenerated: [],

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['dueDate'],
    sortDesc: [true],
  },

  type: 'add',
  loading: false,
  oldPayment: {},
  currentTotal: 0,
  dialogDespesa: false,
  dialogReceita: false,
  dialogPayOff: false,
}
const mutations = make.mutations(state)
const actions = {
  add: ({ state }, type) => {
    state.payment = {
      type,
      companyId: store.get('authStore@currentCompany').id,
      quotas: 1,
      recurrenceEnabled: false,
      recurrenceType: 'monthly',
      recurrenceInterval: 1,
      recurrenceEndDate: null,
    }
    state.payments = []
    state.paymentsGenerated = []
    state.filter.type = type
    state.accountType = type
    state.type = 'add'
    state.payment.quotas = 1
    if (Number(type) === TIPO_DESPESA) {
      state.dialogDespesa = true
    } else {
      state.dialogReceita = true
    }
  },

  edit: ({ state }, payment) => {
    Number(payment.type) === TIPO_DESPESA ? state.dialogDespesa = true : state.dialogReceita = true
    state.loading = true
    state.type = 'edit'
    state.payment = normalizePaymentValues(payment)

    store.dispatch('paymentStore/getById', payment.uid)
      .then(async res => {
        let total = 0
        let desconto = 0
        let acrescimo = 0
        const payments = res.data.data.map(normalizePaymentValues)
        await payments.forEach(element => {
          if (!element.payed) {
            total = total + element.value
          } else {
            total = total + element.valuePayed
          }

          element.dueDate = formatDate(new Date(element.dueDate).toISOString().substr(0, 10))
          element.paymentDate = element.paymentDate ? formatDate(new Date(element.paymentDate).toISOString().substr(0, 10)) : null

          if (element.addition) {
            acrescimo = acrescimo + element.addition
          }
          if (element.discount) {
            desconto = desconto + element.discount
          }

          if (Number(element.sequence) === 1) { // obtem a data da primeira parcela
            state.payment.firstDueDate = state.oldPayment.dueDate = element.dueDate
          }
        })
        state.paymentsGenerated = payments
        state.payment.value = total
        state.payment.discount = desconto
        state.payment.addition = acrescimo
        state.oldPayment.value = state.payment.value
        state.oldPayment.quotas = state.payment.quotas
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  view: ({ state }, payment) => {
    Number(payment.type) === TIPO_DESPESA ? state.dialogDespesa = true : state.dialogReceita = true
    state.loading = true
    state.type = 'view'
    state.payment = normalizePaymentValues(payment)

    store.dispatch('paymentStore/getById', payment.uid)
      .then(async res => {
        let total = 0
        let desconto = 0
        let acrescimo = 0
        const payments = res.data.data.map(normalizePaymentValues)
        await payments.forEach(element => {
          if (!element.payed) {
            total = total + element.value
          } else {
            total = total + element.valuePayed
          }

          element.dueDate = formatDate(new Date(element.dueDate).toISOString().substr(0, 10))
          element.paymentDate = element.paymentDate ? formatDate(new Date(element.paymentDate).toISOString().substr(0, 10)) : null

          if (element.addition) {
            acrescimo = acrescimo + element.addition
          }
          if (element.discount) {
            desconto = desconto + element.discount
          }

          if (Number(element.sequence) === 1) { // obtem a data da primeira parcela
            state.payment.firstDueDate = state.oldPayment.dueDate = element.dueDate
          }
        })
        state.paymentsGenerated = payments
        state.payment.value = total
        state.payment.discount = desconto
        state.payment.addition = acrescimo
        state.oldPayment.value = state.payment.value
        state.oldPayment.quotas = state.payment.quotas
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  addPayOff: ({ state }, item) => {
    state.paymentOff = normalizePaymentValues(item)
    if (!state.paymentOff.valuePayed) {
      state.paymentOff.valuePayed = state.paymentOff.value
    }
    state.dialogPayOff = true
  },

  updateSituation: ({ state }, payment) => {
    state.loading = true
    payment.payed = 'Pago'
    return axios.post(`${process.env.VUE_APP_API_URL}/payment/${payment.uid}`, payment)
      .then((res) => {
        return refreshPayments()
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  payoff: async ({ state }, payment) => {
    state.loading = true
    try {
      const response = await axios.post(`${process.env.VUE_APP_API_URL}/payment-payoff/${payment.id}`, payment)
      showSuccess('Conta quitada com sucesso!')
      await refreshPayments()
      return response
    } finally {
      state.loading = false
    }
  },

  save: ({ state }, payments) => {
    const payment = payments[0] ? payments[0] : payments
    const method = payment.uid ? 'put' : 'post'
    state.loading = true
    return axios[method](`${process.env.VUE_APP_API_URL}/payment/${payment.uid || ''}`, { payments })
      .then((res) => {
        Number(payment.type) === TIPO_DESPESA ? state.dialogDespesa = false : state.dialogReceita = false

        showSuccess('conta salva com sucesso!')
        return refreshPayments()
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  get: async ({ state }, filter = state.filter) => {
    const normalizedFilter = normalizeFilter(filter)
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/payment?${getQueryFromFilter(normalizedFilter)}`)
      .then(res => {
        state.payments = res.data
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getSummary: async ({ state }, filter = state.filter) => {
    const normalizedFilter = normalizeFilter(filter)

    return axios.get(`${process.env.VUE_APP_API_URL}/payment-summary?${getQueryFromFilter(normalizedFilter)}`)
      .then(res => {
        state.paymentSummary = res.data
        return res.data
      })
      .catch(showError)
  },

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/payment/${id}`).catch(showError),

  getByIdSequence: (context, payment) => axios.get(`${process.env.VUE_APP_API_URL}/payment/${payment.uid}/${payment.sequence}`).catch(showError),

  remove: ({ state }, payload) => {
    const id = typeof payload === 'object' ? (payload.uid || payload.id) : payload
    const cancelRecurrence = typeof payload === 'object' ? Boolean(payload.cancelRecurrence) : false
    state.loading = true
    return axios.delete(`${process.env.VUE_APP_API_URL}/payment/${id}?cancelRecurrence=${cancelRecurrence}`)
      .then(_ => {
        showSuccess('conta excluída com sucesso!')
        return refreshPayments()
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },
  cancelPayment: ({ state }, payment) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/payment-cancel/${payment.id}`, payment)
      .then(_ => {
        showSuccess('pagamento removido com sucesso!')
        return refreshPayments()
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },
}
const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
